// ClientStats.jsx - Client Portal Statistics Page
// Matches main dashboard Stats.jsx design, filtered for client's bots only

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import ClientCallStats from '../../components/client/ClientCallStats'
import { useClientAuth } from '../../context/ClientAuthContext'
import { useIncrementalRefresh } from '../../hooks/useAutoRefresh'
import {
  Phone, PhoneForwarded, PhoneOff, PhoneMissed,
  Bot, Clock, BarChart3, RefreshCw, Calendar, ChevronDown,
  FileText, Voicemail, CheckCircle, XCircle, AlertTriangle,
  ChevronLeft, ChevronRight, Search, X, Filter,
  Play, Pause, Volume2, UserX
} from 'lucide-react'
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip
} from 'recharts'
import './ClientPortal.css'

// Disposition config
const DISPOSITION_CONFIG = {
  'transfer-call': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
  'call-transfered': { label: 'Transferred', color: '#10b981', icon: PhoneForwarded },
  'TRANSFER': { label: 'Transfer', color: '#10b981', icon: PhoneForwarded },
  'not-interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
  'not_interested': { label: 'Not Interested', color: '#f59e0b', icon: PhoneOff },
  'do-not-call': { label: 'DNC', color: '#ef4444', icon: PhoneMissed },
  'do_not_call': { label: 'DNC', color: '#ef4444', icon: PhoneMissed },
  'answering-machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
  'answering_machine': { label: 'Voicemail', color: '#8b5cf6', icon: Voicemail },
  'no-answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
  'no_answer': { label: 'No Answer', color: '#6b7280', icon: PhoneOff },
}

const PIE_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#6366f1', '#ec4899', '#14b8a6']
const PAGE_SIZES = [50, 100, 250]

const formatLabel = (str) => {
  if (!str) return 'Unknown'
  if (DISPOSITION_CONFIG[str]) return DISPOSITION_CONFIG[str].label
  return str.replace(/[-_]/g, ' ').split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')
}

const formatDuration = (seconds) => {
  if (!seconds) return '0s'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (mins > 0) return `${mins}m ${secs}s`
  return `${secs}s`
}

const formatDurationShort = (s) => s ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : '0:00'

const formatDateTime = (d) => d ? new Date(d).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
}) : '-'

const getDateString = (date) => date.toISOString().split('T')[0]

export default function ClientStats() {
  const { clientApi } = useClientAuth()

  // Date state
  const [datePreset, setDatePreset] = useState('week')
  const [customDateFrom, setCustomDateFrom] = useState('')
  const [customDateTo, setCustomDateTo] = useState('')
  const [showDatePicker, setShowDatePicker] = useState(false)

  // Tab state
  const [activeTab, setActiveTab] = useState('overview')
  const [statsTick, setStatsTick] = useState(0)   // bump → overview reloads

  // Stats data
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [overview, setOverview] = useState(null)
  const [dispositionStats, setDispositionStats] = useState(null)
  const [dialerStats, setDialerStats] = useState([])
  const [voiceSetStats, setVoiceSetStats] = useState([])

  // Call logs state
  const [callLogs, setCallLogs] = useState([])
  const [callLogsTotal, setCallLogsTotal] = useState(0)
  const [callLogsLoading, setCallLogsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [selectedDisposition, setSelectedDisposition] = useState('')

  // Live call feed state
  const [newCallIds, setNewCallIds] = useState(new Set())
  const latestCallIdRef = useRef(null)

  // Build date params
  const getDateParams = useCallback(() => {
    const today = new Date()

    if (datePreset === 'custom' && customDateFrom && customDateTo) {
      return { from: customDateFrom, to: customDateTo }
    }

    switch (datePreset) {
      case 'today':
        return { from: getDateString(today), to: getDateString(today) }
      case 'yesterday':
        const yesterday = new Date(today - 24 * 60 * 60 * 1000)
        return { from: getDateString(yesterday), to: getDateString(yesterday) }
      case 'week':
        const weekAgo = new Date(today - 7 * 24 * 60 * 60 * 1000)
        return { from: getDateString(weekAgo), to: getDateString(today) }
      case 'month':
        const monthAgo = new Date(today - 30 * 24 * 60 * 60 * 1000)
        return { from: getDateString(monthAgo), to: getDateString(today) }
      default:
        return { from: getDateString(today), to: getDateString(today) }
    }
  }, [datePreset, customDateFrom, customDateTo])

  // Fetch overview stats
  const fetchStats = useCallback(async (showRefreshing = false) => {
    try {
      if (showRefreshing) setRefreshing(true)
      else setLoading(true)

      const params = getDateParams()
      const queryString = `?from=${params.from}&to=${params.to}`

      const [overviewRes, dispoRes, dialerRes, voiceSetRes] = await Promise.all([
        clientApi(`/stats/overview${queryString}`),
        clientApi(`/stats/dispositions${queryString}`),
        clientApi(`/stats/by-dialer${queryString}`),
        clientApi(`/stats/voice-sets${queryString}`)
      ])

      if (overviewRes.success) setOverview(overviewRes.overview)
      if (dispoRes.success) setDispositionStats(dispoRes)
      if (dialerRes.success) setDialerStats(dialerRes.dialers || [])
      if (voiceSetRes.success) setVoiceSetStats(voiceSetRes.voice_sets || [])
    } catch (error) {
      console.error('Stats fetch error:', error)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [clientApi, getDateParams])

  // Fetch call logs
  const fetchCallLogs = useCallback(async (page = 1) => {
    try {
      setCallLogsLoading(true)
      const params = getDateParams()
      const offset = (page - 1) * pageSize

      let queryString = `?from=${params.from}&to=${params.to}&limit=${pageSize}&offset=${offset}`
      if (selectedDisposition) queryString += `&disposition=${selectedDisposition}`

      const res = await clientApi(`/call-logs${queryString}`)

      if (res.success) {
        setCallLogs(res.data || [])
        setCallLogsTotal(res.total || 0)
      }
    } catch (error) {
      console.error('Call logs fetch error:', error)
      setCallLogs([])
      setCallLogsTotal(0)
    } finally {
      setCallLogsLoading(false)
    }
  }, [clientApi, getDateParams, pageSize, selectedDisposition])

  // Fetch new calls incrementally (for live feed)
  const fetchNewCalls = useCallback(async (afterId) => {
    if (!afterId) return null

    try {
      const params = getDateParams()
      let queryString = `?from=${params.from}&to=${params.to}&after_id=${afterId}&limit=50`
      if (selectedDisposition) queryString += `&disposition=${selectedDisposition}`

      const res = await clientApi(`/stats/calls${queryString}`)

      if (res.success && res.calls?.length > 0) {
        // Track new call IDs for highlight animation
        const newIds = new Set(res.calls.map(c => c.id))
        setNewCallIds(prev => new Set([...prev, ...newIds]))

        // Clear highlight after 3 seconds
        setTimeout(() => {
          setNewCallIds(prev => {
            const updated = new Set(prev)
            newIds.forEach(id => updated.delete(id))
            return updated
          })
        }, 3000)

        // Only prepend if on first page (newest calls)
        if (currentPage === 1) {
          setCallLogs(prev => {
            // Prepend new calls, remove duplicates, keep max 500
            const existingIds = new Set(prev.map(c => c.id))
            const uniqueNewCalls = res.calls.filter(c => !existingIds.has(c.id))
            return [...uniqueNewCalls, ...prev].slice(0, 500)
          })
          setCallLogsTotal(prev => prev + res.calls.length)
        }

        return { latest_id: res.calls[0].id }
      }
      return null
    } catch (error) {
      console.error('[ClientStats] Incremental fetch error:', error)
      return null
    }
  }, [clientApi, getDateParams, selectedDisposition, currentPage])

  // Use incremental refresh for live call feed (5 seconds when on calls tab)
  const { setLatestId, resetLatestId } = useIncrementalRefresh(
    fetchNewCalls,
    5000,
    activeTab === 'calls' && currentPage === 1 && !callLogsLoading
  )

  // Update latestCallId when call logs are fetched
  useEffect(() => {
    if (callLogs.length > 0 && callLogs[0]?.id) {
      const maxId = Math.max(...callLogs.map(c => c.id || 0))
      latestCallIdRef.current = maxId
      setLatestId(maxId)
    }
  }, [callLogs, setLatestId])

  // Reset latest ID when filters change
  useEffect(() => {
    resetLatestId()
  }, [selectedDisposition, resetLatestId])

  // Initial load and when date changes
  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  // Fetch call logs when tab changes to calls or filters change
  useEffect(() => {
    if (activeTab === 'calls') {
      fetchCallLogs(currentPage)
    }
  }, [activeTab, currentPage, pageSize, selectedDisposition, fetchCallLogs])

  // Reset page when disposition filter changes
  useEffect(() => {
    setCurrentPage(1)
  }, [selectedDisposition])

  const getDateLabel = () => {
    switch (datePreset) {
      case 'today': return 'Today'
      case 'yesterday': return 'Yesterday'
      case 'week': return 'Last 7 Days'
      case 'month': return 'Last 30 Days'
      case 'custom':
        if (customDateFrom && customDateTo) return `${customDateFrom} - ${customDateTo}`
        return 'Custom Range'
      default: return 'Select Date'
    }
  }

  // Chart data - use DISPOSITION_CONFIG colors for consistency with KPI blocks
  const pieChartData = dispositionStats?.dispositions?.map((d, i) => ({
    name: formatLabel(d.disposition),
    value: d.count,
    percentage: d.percentage,
    color: DISPOSITION_CONFIG[d.disposition]?.color || PIE_COLORS[i % PIE_COLORS.length]
  })) || []

  const transferStats = overview?.transfers || { count: 0, percentage: 0 }
  const totalPages = Math.ceil(callLogsTotal / pageSize)

  const getDispoConfig = (d) => DISPOSITION_CONFIG[d] || { label: d || 'Unknown', color: '#6b7280', icon: Phone }

  if (loading) {
    return (
      <div className="client-loading">
        <div className="loading-spinner"></div>
        <p>Loading statistics...</p>
      </div>
    )
  }

  return (
    <div className="client-stats-page">
      {/* Header */}
      <div className="client-stats-header">
        <div className="client-stats-header-left">
          <h1>
            <BarChart3 size={24} />
            Statistics
          </h1>
        </div>
        <div className="client-stats-header-right">
          <div className="client-date-dropdown">
            <button className="client-date-btn" onClick={() => setShowDatePicker(!showDatePicker)}>
              <Calendar size={16} />
              {getDateLabel()}
              <ChevronDown size={16} />
            </button>
            {showDatePicker && (
              <DatePickerDropdown
                datePreset={datePreset}
                setDatePreset={setDatePreset}
                customDateFrom={customDateFrom}
                setCustomDateFrom={setCustomDateFrom}
                customDateTo={customDateTo}
                setCustomDateTo={setCustomDateTo}
                onClose={() => setShowDatePicker(false)}
              />
            )}
          </div>
          <button className="client-refresh-btn" onClick={() => { fetchStats(true); setStatsTick(n => n + 1) }} disabled={refreshing}>
            <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="client-stats-tabs">
        <button
          className={`client-stats-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <BarChart3 size={16} />
          Overview
        </button>
        <button
          className={`client-stats-tab ${activeTab === 'calls' ? 'active' : ''}`}
          onClick={() => setActiveTab('calls')}
        >
          <FileText size={16} />
          Call Logs
        </button>
      </div>

      {activeTab === 'overview' ? (
        /* Same stats as the dashboard's Monitoring page, for this client only */
        <ClientCallStats key={statsTick} {...getDateParams()} />
      ) : (
        /* Call Logs Tab */
        <div className="client-call-logs">
          {/* Disposition Filter Cards */}
          {dispositionStats?.dispositions?.length > 0 && (
            <div className="client-dispo-row">
              {dispositionStats.dispositions.slice(0, 6).map((stat, i) => {
                const config = getDispoConfig(stat.disposition)
                const Icon = config.icon
                const isActive = selectedDisposition === stat.disposition
                return (
                  <button
                    key={i}
                    className={`client-dispo-card ${isActive ? 'active' : ''}`}
                    style={{ '--accent': config.color }}
                    onClick={() => setSelectedDisposition(isActive ? '' : stat.disposition)}
                  >
                    <Icon size={18} />
                    <span className="client-dispo-count">{stat.count}</span>
                    <span className="client-dispo-label">{config.label}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Call Logs Table */}
          <div className="client-table-container">
            <div className="client-table-header">
              <h2>
                <Phone size={18} />
                Call Logs
                <span className="client-call-count">{callLogsTotal.toLocaleString()} calls</span>
                {currentPage === 1 && !callLogsLoading && (
                  <span className="client-live-indicator">
                    <span className="live-dot"></span>
                    Live
                  </span>
                )}
              </h2>
              {selectedDisposition && (
                <button className="client-clear-filter" onClick={() => setSelectedDisposition('')}>
                  <X size={14} /> Clear filter
                </button>
              )}
            </div>

            {callLogsLoading ? (
              <div className="client-loading-inline">
                <div className="loading-spinner"></div>
                <span>Loading calls...</span>
              </div>
            ) : callLogs.length === 0 ? (
              <div className="client-empty-state">
                <Phone size={40} />
                <p>No calls found</p>
                <span>Try adjusting your date range or filters</span>
              </div>
            ) : (
              <table className="client-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Disposition</th>
                    <th>Duration</th>
                    <th>Recording</th>
                    <th>Hung Up</th>
                    <th>Step</th>
                    <th>SIP</th>
                    <th>Dialer</th>
                  </tr>
                </thead>
                <tbody>
                  {callLogs.map((call, i) => {
                    const config = getDispoConfig(call.disposition)
                    const Icon = config.icon
                    const isNew = newCallIds.has(call.id)
                    return (
                      <tr key={call.id || i} className={isNew ? 'call-row-new' : ''} data-call-id={call.id}>
                        <td className="td-time">{formatDateTime(call.call_end)}</td>
                        <td>
                          <span className="client-dispo-badge" style={{ '--c': config.color }}>
                            <Icon size={12} />
                            {config.label}
                          </span>
                        </td>
                        <td className="td-duration">{formatDurationShort(call.call_duration)}</td>
                        <td className="td-recording">
                          <RecordingPlayer src={call.recording_url} duration={call.recording_duration} />
                        </td>
                        <td className="td-hungup">
                          {call.hung_up_by === 'caller' ? (
                            <span className="hungup-badge caller"><UserX size={12} /> Caller</span>
                          ) : call.hung_up_by === 'agent' ? (
                            <span className="hungup-badge agent"><Bot size={12} /> Agent</span>
                          ) : (
                            <span className="hungup-badge unknown">—</span>
                          )}
                        </td>
                        <td className="td-step">{call.script_step_reached || '-'}</td>
                        <td className="td-sip">{call.sip_id}</td>
                        <td className="td-dialer">{call.dialer_name || '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}

            {/* Pagination */}
            {totalPages > 0 && !callLogsLoading && (
              <div className="client-pagination">
                <div className="client-page-info">
                  Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, callLogsTotal)} of {callLogsTotal.toLocaleString()}
                </div>
                <div className="client-page-controls">
                  <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1) }}>
                    {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                  </select>
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>First</button>
                  <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                    <ChevronLeft size={16} />
                  </button>
                  <span>{currentPage} / {totalPages}</span>
                  <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                    <ChevronRight size={16} />
                  </button>
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>Last</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        .client-stats-page {
          animation: fadeIn 0.3s ease;
        }

        .client-stats-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--space-lg);
        }

        .client-stats-header h1 {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .client-stats-header-right {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .client-date-dropdown {
          position: relative;
        }

        .client-date-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          color: var(--text-primary);
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-date-btn:hover {
          border-color: var(--primary);
        }

        .client-refresh-btn {
          padding: 10px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-refresh-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
        }

        .client-refresh-btn .spin {
          animation: spin 1s linear infinite;
        }

        /* Tabs */
        .client-stats-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: var(--space-lg);
          padding: 4px;
          background: var(--bg-secondary);
          border-radius: var(--radius-md);
          width: fit-content;
        }

        .client-stats-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          background: transparent;
          border: none;
          border-radius: var(--radius-sm);
          color: var(--text-muted);
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-stats-tab:hover {
          color: var(--text-primary);
        }

        .client-stats-tab.active {
          background: var(--bg-card);
          color: var(--primary);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        /* Charts Row */
        .client-charts-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-lg);
          margin-top: var(--space-lg);
        }

        .client-chart-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 20px;
        }

        .client-chart-card h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0 0 16px 0;
        }

        .client-chart-with-legend {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .client-pie-container {
          flex: 0 0 200px;
        }

        .client-chart-legend {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .client-legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8125rem;
        }

        .client-legend-color {
          width: 10px;
          height: 10px;
          border-radius: 2px;
          flex-shrink: 0;
        }

        .client-legend-label {
          flex: 1;
          color: var(--text-secondary);
        }

        .client-legend-value {
          font-weight: 600;
          color: var(--text-primary);
        }

        .client-no-data {
          text-align: center;
          padding: 40px;
          color: var(--text-muted);
        }

        /* Dialer List */
        .client-dialer-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .client-dialer-item {
          position: relative;
          padding: 12px 16px;
          background: var(--bg-secondary);
          border-radius: var(--radius-md);
          overflow: hidden;
        }

        .client-dialer-bar {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          background: linear-gradient(90deg, rgba(59, 130, 246, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%);
          border-left: 3px solid var(--primary);
        }

        .client-dialer-content {
          position: relative;
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .client-dialer-rank {
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--text-muted);
          width: 20px;
        }

        .client-dialer-name {
          flex: 1;
          font-weight: 500;
          color: var(--text-primary);
        }

        .client-dialer-metrics {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .client-dialer-metrics .metric {
          font-size: 0.875rem;
          color: var(--text-secondary);
        }

        .client-dialer-metrics .metric.success {
          color: var(--success);
        }

        .client-dialer-metrics .metric-rate {
          font-weight: 600;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .client-dialer-metrics .metric-rate.good {
          background: rgba(34, 197, 94, 0.15);
          color: #22c55e;
        }

        .client-dialer-metrics .metric-rate.mid {
          background: rgba(245, 158, 11, 0.15);
          color: #f59e0b;
        }

        .client-dialer-metrics .metric-rate.low {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        /* Call Logs */
        .client-call-logs {
          animation: fadeIn 0.3s ease;
        }

        .client-dispo-row {
          display: flex;
          gap: 12px;
          margin-bottom: var(--space-lg);
          flex-wrap: wrap;
        }

        .client-dispo-card {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding: 12px 20px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          cursor: pointer;
          transition: all 0.2s;
          color: var(--accent);
        }

        .client-dispo-card:hover {
          border-color: var(--accent);
          transform: translateY(-2px);
        }

        .client-dispo-card.active {
          background: color-mix(in srgb, var(--accent) 15%, transparent);
          border-color: var(--accent);
        }

        .client-dispo-count {
          font-size: 1.25rem;
          font-weight: 700;
        }

        .client-dispo-label {
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .client-call-count {
          font-size: 0.875rem;
          font-weight: 400;
          color: var(--text-muted);
          margin-left: 12px;
        }

        .client-clear-filter {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 6px 12px;
          background: rgba(239, 68, 68, 0.1);
          border: none;
          border-radius: var(--radius-sm);
          color: var(--danger);
          font-size: 0.8125rem;
          cursor: pointer;
        }

        .client-loading-inline {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          padding: 60px;
          color: var(--text-muted);
        }

        .client-empty-state {
          text-align: center;
          padding: 60px;
          color: var(--text-muted);
        }

        .client-empty-state p {
          margin: 16px 0 8px;
          font-weight: 500;
        }

        .client-empty-state span {
          font-size: 0.875rem;
        }

        .client-dispo-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
          background: color-mix(in srgb, var(--c) 15%, transparent);
          color: var(--c);
        }

        .td-time {
          white-space: nowrap;
          font-size: 0.8125rem;
        }

        .td-duration {
          font-family: 'SF Mono', monospace;
          font-size: 0.8125rem;
        }

        .td-step {
          text-align: center;
          color: var(--text-muted);
        }

        .td-sip {
          font-family: 'SF Mono', monospace;
          font-weight: 500;
          color: var(--primary);
        }

        .td-dialer {
          color: var(--text-secondary);
        }

        .hungup-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.6875rem;
          font-weight: 500;
        }

        .hungup-badge.caller {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }

        .hungup-badge.agent {
          background: rgba(59, 130, 246, 0.1);
          color: #3b82f6;
        }

        .hungup-badge.unknown {
          color: var(--text-muted);
        }

        /* Pagination */
        .client-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-top: 1px solid var(--border-color);
          margin-top: 16px;
        }

        .client-page-info {
          font-size: 0.8125rem;
          color: var(--text-muted);
        }

        .client-page-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .client-page-controls select {
          padding: 6px 10px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-size: 0.8125rem;
        }

        .client-page-controls button {
          padding: 6px 12px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
          font-size: 0.8125rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-page-controls button:hover:not(:disabled) {
          border-color: var(--primary);
          color: var(--primary);
        }

        .client-page-controls button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .client-page-controls span {
          font-size: 0.8125rem;
          color: var(--text-secondary);
          padding: 0 8px;
        }

        /* Recording Player */
        .client-play-btn {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 8px;
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 4px;
          color: #3b82f6;
          font-size: 0.75rem;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-play-btn:hover {
          background: rgba(59, 130, 246, 0.2);
        }

        .client-play-btn.playing {
          background: rgba(34, 197, 94, 0.1);
          border-color: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }

        .client-no-recording {
          color: var(--text-muted);
        }

        .client-play-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid rgba(59, 130, 246, 0.3);
          border-top-color: #3b82f6;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* New call highlight animation */
        @keyframes highlightNew {
          0% { background-color: rgba(34, 197, 94, 0.25); }
          100% { background-color: transparent; }
        }

        .call-row-new {
          animation: highlightNew 3s ease-out;
        }

        .call-row-new td {
          position: relative;
        }

        .call-row-new td:first-child::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 3px;
          background: var(--success);
          border-radius: 0 2px 2px 0;
        }

        /* Live indicator */
        .client-live-indicator {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-left: 12px;
          padding: 4px 10px;
          background: rgba(34, 197, 94, 0.1);
          border-radius: var(--radius-full);
          font-size: 0.6875rem;
          font-weight: 600;
          color: var(--success);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .live-dot {
          width: 6px;
          height: 6px;
          background: var(--success);
          border-radius: 50%;
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }

        @media (max-width: 768px) {
          .client-charts-row {
            grid-template-columns: 1fr;
          }

          .client-chart-with-legend {
            flex-direction: column;
          }
        }

        /* Voice Sets Performance Section */
        .client-voice-sets-section {
          margin-top: var(--space-lg);
        }

        .client-vs-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          overflow: hidden;
        }

        .client-vs-card-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-color);
        }

        .client-vs-card-header h3 {
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin: 0;
        }

        .client-vs-count {
          font-size: 0.75rem;
          color: var(--text-muted);
          background: var(--bg-secondary);
          padding: 4px 10px;
          border-radius: var(--radius-full);
        }

        .client-vs-list {
          max-height: 420px;
          overflow-y: auto;
          padding: 8px;
        }

        .client-vs-list::-webkit-scrollbar {
          width: 4px;
        }

        .client-vs-list::-webkit-scrollbar-thumb {
          background: var(--border-color);
          border-radius: 4px;
        }

        .client-vs-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: var(--radius-md);
          transition: background 0.15s;
        }

        .client-vs-row:hover {
          background: var(--bg-secondary);
        }

        .client-vs-rank {
          width: 22px;
          font-size: 0.75rem;
          font-weight: 700;
          color: var(--text-muted);
          text-align: center;
          flex-shrink: 0;
        }

        .client-vs-info {
          flex: 1;
          min-width: 0;
        }

        .client-vs-name {
          font-size: 0.875rem;
          font-weight: 500;
          color: var(--text-primary);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-bottom: 4px;
        }

        .client-vs-progress {
          height: 4px;
          background: var(--bg-secondary);
          border-radius: 2px;
          overflow: hidden;
          max-width: 160px;
        }

        .client-vs-progress-bar {
          height: 100%;
          border-radius: 2px;
          transition: width 0.4s ease;
        }

        .client-vs-metrics {
          display: flex;
          align-items: center;
          gap: 14px;
          flex-shrink: 0;
        }

        .client-vs-metric {
          font-size: 0.8125rem;
          color: var(--text-secondary);
          white-space: nowrap;
        }

        .client-vs-metric small {
          color: var(--text-muted);
          font-size: 0.6875rem;
        }

        .client-vs-metric.success {
          color: var(--success);
        }

        .client-vs-rate-badge {
          font-size: 0.75rem;
          font-weight: 600;
          padding: 3px 8px;
          border-radius: 4px;
          white-space: nowrap;
        }
      `}</style>
    </div>
  )
}

// Date Picker Dropdown Component
function DatePickerDropdown({ datePreset, setDatePreset, customDateFrom, setCustomDateFrom, customDateTo, setCustomDateTo, onClose }) {
  return (
    <div className="client-date-picker">
      <div className="client-date-picker__presets">
        {[
          { value: 'today', label: 'Today' },
          { value: 'yesterday', label: 'Yesterday' },
          { value: 'week', label: 'Last 7 Days' },
          { value: 'month', label: 'Last 30 Days' },
        ].map(p => (
          <button
            key={p.value}
            className={datePreset === p.value ? 'active' : ''}
            onClick={() => { setDatePreset(p.value); onClose(); }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="client-date-picker__divider">or select custom range</div>
      <div className="client-date-picker__inputs">
        <input
          type="date"
          value={customDateFrom}
          onChange={(e) => { setCustomDateFrom(e.target.value); setDatePreset('custom'); }}
        />
        <span>to</span>
        <input
          type="date"
          value={customDateTo}
          onChange={(e) => { setCustomDateTo(e.target.value); setDatePreset('custom'); }}
        />
      </div>
      {datePreset === 'custom' && customDateFrom && customDateTo && (
        <button className="client-date-picker__apply" onClick={onClose}>Apply</button>
      )}

      <style>{`
        .client-date-picker {
          position: absolute;
          top: calc(100% + 8px);
          right: 0;
          min-width: 280px;
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-lg);
          padding: 16px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.3);
          z-index: 100;
        }

        .client-date-picker__presets {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .client-date-picker__presets button {
          padding: 10px 12px;
          background: transparent;
          border: none;
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
          font-size: 0.875rem;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s;
        }

        .client-date-picker__presets button:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .client-date-picker__presets button.active {
          background: rgba(59, 130, 246, 0.1);
          color: var(--primary);
        }

        .client-date-picker__divider {
          margin: 12px 0;
          text-align: center;
          font-size: 0.75rem;
          color: var(--text-muted);
        }

        .client-date-picker__inputs {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .client-date-picker__inputs input {
          flex: 1;
          padding: 8px 10px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-size: 0.8125rem;
        }

        .client-date-picker__inputs span {
          color: var(--text-muted);
          font-size: 0.8125rem;
        }

        .client-date-picker__apply {
          width: 100%;
          margin-top: 12px;
          padding: 10px;
          background: var(--primary);
          border: none;
          border-radius: var(--radius-sm);
          color: white;
          font-weight: 500;
          cursor: pointer;
        }
      `}</style>
    </div>
  )
}

// Custom Pie Tooltip
function CustomPieTooltip({ active, payload }) {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
        borderRadius: '8px',
        padding: '8px 12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
      }}>
        <p style={{ margin: 0, fontWeight: 500, color: 'var(--text-primary)' }}>{data.name}</p>
        <p style={{ margin: '4px 0 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
          {data.value} calls ({data.percentage}%)
        </p>
      </div>
    )
  }
  return null
}

// Recording Player Component
function RecordingPlayer({ src, duration }) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const audioRef = useState(null)

  if (!src) {
    return <span className="client-no-recording">—</span>
  }

  const togglePlay = async (e) => {
    e.stopPropagation()
    const audio = e.currentTarget.querySelector('audio')
    if (!audio) return

    try {
      if (isPlaying) {
        audio.pause()
        setIsPlaying(false)
      } else {
        document.querySelectorAll('audio').forEach(a => a.pause())
        setIsLoading(true)
        await audio.play()
        setIsPlaying(true)
        setIsLoading(false)
      }
    } catch (err) {
      console.error('Playback error:', err)
      setIsLoading(false)
    }
  }

  return (
    <button
      className={`client-play-btn ${isPlaying ? 'playing' : ''}`}
      onClick={togglePlay}
      title={duration ? `${duration}s recording` : 'Play recording'}
    >
      <audio
        src={src}
        preload="none"
        onEnded={() => setIsPlaying(false)}
        onPause={() => setIsPlaying(false)}
      />
      {isLoading ? (
        <span className="client-play-spinner" />
      ) : isPlaying ? (
        <Pause size={14} />
      ) : (
        <Play size={14} />
      )}
      {duration > 0 && <span>{duration}s</span>}
    </button>
  )
}
